import React, { useCallback, useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { MoneyFact, moneyFactsApi, STALE_AFTER_DAYS } from '../../api/moneyFacts';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';

/**
 * The figures Ibra quotes as current.
 *
 * Ibra answers "what's the CBN rate?" and "how much is the dollar?" now, and
 * a model's memory of those is months old. What is filled in here is what it
 * quotes instead — always with the date beside it, so the date matters as
 * much as the number. A figure left unchecked for more than
 * {@link STALE_AFTER_DAYS} days is still quoted, as "may be out of date".
 */
type Draft = { value: string; asOf: string; source: string };

function draftOf(fact: MoneyFact): Draft {
  return { value: fact.value ?? '', asOf: fact.asOf ?? '', source: fact.source ?? '' };
}

function ageInDays(asOf: string | null, now: Date = new Date()): number | null {
  if (!asOf) return null;
  return Math.floor((now.getTime() - new Date(`${asOf}T00:00:00Z`).getTime()) / 86_400_000);
}

export const MoneyFigures: React.FC = () => {
  usePage('Money Figures');
  const toast = useToast();
  const [facts, setFacts] = useState<MoneyFact[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await moneyFactsApi.list();
      setFacts(rows);
      setDrafts(Object.fromEntries(rows.map((f) => [f.key, draftOf(f)])));
    } catch (err) {
      toast.error(err, 'Could not load the money figures.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(fact: MoneyFact) {
    const draft = drafts[fact.key];
    if (!draft) return;
    if (draft.value.trim() && !draft.asOf) {
      toast.error('Say the date this figure was true.');
      return;
    }
    setSaving(fact.key);
    try {
      const saved = await moneyFactsApi.update(fact.key, {
        value: draft.value.trim(),
        ...(draft.asOf ? { asOf: draft.asOf } : {}),
        source: draft.source.trim(),
      });
      setFacts((rows) => rows.map((r) => (r.key === saved.key ? saved : r)));
      setDrafts((d) => ({ ...d, [saved.key]: draftOf(saved) }));
      toast.success(saved.value ? `${saved.label} updated.` : `${saved.label} withdrawn from Ibra.`);
    } catch (err) {
      toast.error(err, 'Could not save that figure.');
    } finally {
      setSaving(null);
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={28} />
      </div>
    );
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 960 }}>
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--neutral-600)' }}>
        Ibra quotes these when somebody asks about rates and prices, always with the date beside
        the figure. Fill in the date the figure was true, not today’s. Figures older than{' '}
        {STALE_AFTER_DAYS} days are quoted as “may be out of date”. Clear a value to stop Ibra
        using it.
      </p>

      {facts.map((fact) => {
        const draft = drafts[fact.key] ?? draftOf(fact);
        const age = ageInDays(fact.asOf);
        const changed =
          draft.value !== (fact.value ?? '') ||
          draft.asOf !== (fact.asOf ?? '') ||
          draft.source !== (fact.source ?? '');
        const set = (patch: Partial<Draft>) =>
          setDrafts((d) => ({ ...d, [fact.key]: { ...draft, ...patch } }));

        return (
          <Card key={fact.key}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}
            >
              <strong style={{ fontSize: 15, color: 'var(--neutral-800)' }}>{fact.label}</strong>
              {!fact.value ? (
                <Badge variant="neutral">Not given to Ibra</Badge>
              ) : age !== null && age > STALE_AFTER_DAYS ? (
                <Badge variant="warning">{age} days old — quoted as may be out of date</Badge>
              ) : (
                <Badge variant="success">Live</Badge>
              )}
            </div>
            {fact.hint ? (
              <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--neutral-500)' }}>{fact.hint}</p>
            ) : null}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(120px, 1fr) minmax(150px, 1fr) minmax(160px, 1.4fr) auto',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <Input
                label="Figure"
                placeholder="27.25%"
                value={draft.value}
                onChange={(e) => set({ value: e.target.value })}
              />
              <Input
                label="True as of"
                type="date"
                max={today}
                value={draft.asOf}
                onChange={(e) => set({ asOf: e.target.value })}
              />
              <Input
                label="Source"
                placeholder="CBN MPC communiqué"
                value={draft.source}
                onChange={(e) => set({ source: e.target.value })}
              />
              <Button
                disabled={!changed || saving === fact.key}
                onClick={() => void save(fact)}
                aria-label={`Save ${fact.label}`}
              >
                <Save size={15} /> {saving === fact.key ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
